import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import * as moment from 'moment';
import { ChickenTestDialogService } from '../dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { CustomerResponses } from '../../../core/charterAlternateTimeRequest';
import { CustomerDAO } from '../../../core/services/dao/customerDAO';
import { Subscription } from 'rxjs';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { Offer } from 'anchor-shared/lib/model/entity/offer';
import { OfferDAO } from '../../../core/services/dao/offerDAO';
import { Router } from '@angular/router';
import { SingleDigitTimeMeridianFormat } from '../../../core/models/interface/time-and-date/time-and-date-formatting-constants';
import { buildRouteEnding } from '../../../core/pure-functions/navigation/buyflow-vessel-route-builder/buyflow-vessel-route-builder';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import {
  getAddOnsTotal,
  getAddOnsTotalFromPricingMetaData,
} from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { PayoutDAO } from '../../../core/services/dao/payoutDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { VesselRateDAO } from '../../../core/services/dao/vesselRateDAO';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { CustomerReviewStatus } from 'anchor-shared/lib/model/enum/customer-review-status';

@Component({
  selector: 'app-my-charters-card',
  templateUrl: './my-charters-card.component.html',
  styleUrls: ['./my-charters-card.component.scss'],
})
export class MyChartersCardComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Input()
  charter: Charter;

  @Input()
  offer: Offer;

  @Input()
  charterStatus: string;

  @Input()
  primaryPayment: Payment;

  @Input()
  isMobile: boolean;

  @Output()
  cancelCharterEvent = new EventEmitter<number>();

  @Output()
  navigateToGenericPathEvent = new EventEmitter<string>();

  customerResponse = CustomerResponses;
  customerCost: number;
  charterBaseFee: number;
  promoTotal: number = 0;
  isWithinWeekOfCharter: boolean = false;
  completedReview: boolean = false;

  subs: Subscription[] = [];

  constructor(
    private chickenTestDialog: ChickenTestDialogService,
    private CustomerDAO: CustomerDAO,
    private offerDAO: OfferDAO,
    private charterDAO: CharterDAO,
    private payoutDAO: PayoutDAO,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.charter.pricingMetaData.legacyPricing) {
      this.charterDAO
        .getCharterBaseFee(this.charter.id)
        .subscribe(({ baseFee }) => {
          this.charterBaseFee = baseFee;
          this.calculateCustomerCost();
        });
    } else {
      this.calculateCustomerCost();
    }

    if (this.charterStatus === 'past') {
      const timeDiff = Math.abs(
        new Date().getTime() - new Date(this.charter.date).getTime(),
      );
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      this.isWithinWeekOfCharter = daysDiff <= 7;

      this.completedReview = [
        CustomerReviewStatus.Complete,
        CustomerReviewStatus.System,
      ].includes(this.charter.customerReviews?.status);
    }
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // data

  calculateCustomerCost() {
    const discount = this.calculateDiscount();
    this.calculatePromo();

    if (this.offer && this.offer.customOriginalRates) {
      this.setVesselPricing();
    } else {
      let pricingMetaData = this.charter.pricingMetaData;
      if (this.offer?.customAddOns) {
        pricingMetaData.addOns = this.offer.customAddOns;
      }

      this.customerCost =
        (this.charter.charterBaseFee || this.charterBaseFee) +
        this.charter.serviceTotal +
        getAddOnsTotalFromPricingMetaData(
          this.charter.pricingMetaData,
          null,
          true,
        ) -
        discount -
        this.promoTotal;
    }
  }

  calculateDiscount() {
    return (
      Number(
        this.charter.discounts?.reduce((acc, curr) => acc + curr.amount, 0),
      ) || 0
    );
  }

  calculatePromo() {
    if (this.offer && this.charter.pricingMetaData?.promoCode) {
      const discountableAmount = (
        this.offer.customOriginalRates || this.charter.originalRates
      ).reduce((acc, curr) => {
        if (
          [RateType.Owner, RateType.Captain, RateType.Crew].includes(
            curr.rateType,
          )
        ) {
          acc +=
            Number(
              curr.hourlyRate * (this.offer.duration || this.charter.duration),
            ) *
            (1 + curr.markupPercentage);
        }
        return acc;
      }, 0);

      const { amount, percentageBased, promoDiscountAmount } =
        this.charter.pricingMetaData.promoCode;
      if (percentageBased) {
        this.promoTotal = (amount / 100) * discountableAmount;
      } else {
        this.promoTotal = promoDiscountAmount || 0;
      }
    } else {
      this.promoTotal =
        this.charter.pricingMetaData.promoCode?.promoDiscountAmount || 0;
    }
  }

  declineOffer() {
    this.chickenTestDialog
      .open(
        'Are you sure you want to decline this special offer? Once you decline, this offer will be permanently cancelled.',
        'Decline Offer',
        'Go Back',
        this.isMobile ? 100 : 40,
        'Declining all offers will result in a cancelled charter.',
        'Decline Offer',
        null,
        null,
        false,
      )
      .subscribe((declined) => {
        if (declined) {
          this.offerDAO.declineOffer(this.offer.id).subscribe((offers) => {
            this.offer.status = 'declined';
            if (offers.every((o) => o.status)) {
              this.cancelCharterEvent.emit(this.charter.id);
            }
          });
        }
      });
  }

  cancelCharter() {
    this.chickenTestDialog
      .open(
        'Are you sure you want to cancel your charter?',
        'Yes, cancel',
        "No, don't cancel",
        null,
        'Read our cancellation policy <a href="https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/cancellation-policy-new.pdf" target="_blank">here</a>',
      )
      .subscribe((cancelled) => {
        if (cancelled) {
          this.cancelCharterEvent.emit(this.charter.id);
        }
      });
  }

  // utility

  navigateToCharterNewStartTimes() {
    const route = `customer/charter-new-start-time/${this.charter.id}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  navigateToCharterDetails() {
    const route = `customer/charter-details/${this.charter.id}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  navigateToBoatListing() {
    const route = `vessels/yacht-and-boat-rentals/${buildRouteEnding(
      this.offer?.vessel || this.charter.boat,
    )}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  goToCharterPayment() {
    const customerSub = this.CustomerDAO.getSessionToken().subscribe(
      (session) => {
        const route = `/charters/${this.charter.id}/payments/${this.primaryPayment.id}/${session.sessionToken}`;
        this.navigateToGenericPathEvent.emit(route);
      },
    );
    this.subs.push(customerSub);
  }

  goToCharterDetails(charterId) {
    const route = `customer/charter-details/${charterId}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  // note: this is the new charter details page, eventually will transition to only using this page
  goToOfferDetails() {
    const route = `customer/charter-details/${this.offer.charterId}/${this.offer.id}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  goToReview(charterId) {
    const route = `customer/review/${charterId}`;
    this.navigateToGenericPathEvent.emit(route);
  }

  showCancelButton() {
    return (
      this.charterStatus !== 'upcoming' &&
      this.charterStatus !== 'past' &&
      this.charterStatus !== 'cancelled'
    );
  }

  getDate(type) {
    const date = this.offer ? this.offer.dateAlt : this.charter.date;
    const time = this.offer ? this.offer.timeAlt : this.charter.startTime;
    const formattedDate = moment(date + ' ' + time, 'YYYY-MM-DD hh:mm:a');

    if (type === 'end') {
      formattedDate.add(this.offer?.duration || this.charter.duration, 'hours');
    }

    return formattedDate.format('MMM D');
  }

  getTime(type) {
    const time = this.offer ? this.offer.timeAlt : this.charter.startTime;
    const formattedTime = moment(time, SingleDigitTimeMeridianFormat);

    if (type === 'end') {
      formattedTime.add(this.offer?.duration || this.charter.duration, 'hours');
    }

    return formattedTime.format('h:mm a');
  }

  setVesselPricing(): void {
    const charterWithDuration = { ...this.charter };
    if (this.offer?.duration) {
      charterWithDuration.duration = this.offer.duration;
    }
    this.payoutDAO
      .createBOMockPayoutsForPricingDetails(
        this.offer.customOriginalRates,
        charterWithDuration,
      )
      .subscribe(async (mockPayouts) => {
        const applicablePayouts = mockPayouts.filter(
          (x) =>
            x.name !== 'Affiliate User' && (x.type !== 'addOn' || !this.offer),
        );
        const price = this.getBuyflowTotal(applicablePayouts);
        this.customerCost = price - this.promoTotal;
      });
  }

  getBuyflowTotal(payouts): number {
    let total = 0;
    payouts.map((payout) => {
      if (payout.name === 'Booking Fee') {
        total += Number(payout.basePay);
      } else if (payout.type === 'owner') {
        total += parseFloat(payout.basePay) * (1 + payout.markupPercentage);
      } else if (payout.taxPercentage > 0) {
        total += this.calculatePayoutWithMarkup(payout);
      } else {
        total += this.calculatePayoutWithMarkup(payout);
      }
    });

    const requiredAddOns = this.offer.customAddOns;
    total += getAddOnsTotal(requiredAddOns || [], 'required', true);

    return Number(total.toFixed(2));
  }

  calculatePayoutWithMarkup(payout: Payout): number {
    let value = parseFloat(payout.actualPay);
    if (payout.markupPercentage > 0) {
      value += value * payout.markupPercentage;
    }
    return value;
  }
}
